import classNames from 'classnames';
import React, { useState } from 'react';
import { Button, Modal } from '@meitu/saas-ui';
import IconFont from '@meitu/xdesign-icon-font';
import { useLocaleStore } from '@/i18n';
import { ProductRetouchingPanel } from '../product-retouching-panel';
import { PRODUCT_TYPES } from './constants';
import styles from './style.module.less';

type Props = {
    imageURL: string;
    show: boolean;
    commercialStrategy?: 'editor_ai_goods_fix' | 'commerce_ai_goods_fix';
    onConfirm: (url: string) => void;
    onClose: () => void;
    onBeforeApply: () => Promise<boolean>;
    onAfterApply: (url: string) => void;
    onApplyFailed?: () => void;
    onApplyFinally?: () => void;
    onModalClose?: () => void;
};

export const ProductRetouchingModal: React.FC<Props> = ({
    imageURL,
    show,
    commercialStrategy = 'editor_ai_goods_fix',
    onConfirm,
    onClose,
    onBeforeApply,
    onAfterApply,
    onApplyFailed,
    onApplyFinally,
    onModalClose,
}) => {
    const { $t } = useLocaleStore();
    const [resultImageURL, setResultImageURL] = useState('');
    const [showOriginalImage, setShowOriginalImage] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleConfirm = () => {
        setLoading(false);
        setResultImageURL('');
        onConfirm(resultImageURL);
    };

    const handleCancel = () => {
        setLoading(false);
        setResultImageURL('');
        onClose();
    };

    const handleAfterApply = (url: string) => {
        setResultImageURL(url);
        onAfterApply(url);
    };

    const handleApplyFinally = () => {
        setLoading(false);
        onApplyFinally?.();
    };

    const handleBeforeApply = async () => {
        const flag = await onBeforeApply();
        if (!flag) return false;
        setLoading(true);
        return true;
    };

    return (
        <Modal
            className={styles.modal}
            width={960}
            open={show}
            closable={false}
            centered
            destroyOnClose
            maskClosable={false}
            keyboard={false}
            footer={null}
            afterClose={onModalClose}
        >
            <>
                <div className={styles.header}>
                    <div className={styles.title}>{$t((v) => v.productRetouching.productRetouching)}</div>

                    <div className={styles.btns}>
                        <Button className={classNames(styles.btn, styles.cancel)} onClick={handleCancel}>
                            {$t((v) => v.panel.imagePanel.cancel)}
                        </Button>
                        <Button
                            type="primary"
                            className={classNames(styles.btn, styles.confirm)}
                            onClick={handleConfirm}
                        >
                            {$t((v) => v.panel.imagePanel.complete)}
                        </Button>
                    </div>
                </div>

                <div className={styles.content}>
                    <div className={styles.left}>
                        <ProductRetouchingPanel
                            imageURL={imageURL}
                            commercialStrategy={commercialStrategy}
                            productTypes={PRODUCT_TYPES()}
                            onBeforeApply={handleBeforeApply}
                            onAfterApply={handleAfterApply}
                            onApplyFailed={onApplyFailed}
                            onApplyFinally={handleApplyFinally}
                        />
                    </div>
                    <div className={styles.right}>
                        <div className={styles.imageWrap}>
                            <img src={showOriginalImage ? imageURL : resultImageURL || imageURL} alt="" />
                        </div>
                        {resultImageURL && (
                            <div
                                className={styles.compare}
                                onMouseDown={() => setShowOriginalImage(true)}
                                onMouseUp={() => setShowOriginalImage(false)}
                            >
                                <IconFont className={styles.icon} name="CompareBold" fontSize={16} />
                                <span>{$t((v) => v.productRetouching.contrast)}</span>
                            </div>
                        )}
                    </div>
                    {loading && <div className={styles.mask} />}
                </div>
            </>
        </Modal>
    );
};
